import React from "react";
import { useAuth, useLoginWithRedirect } from "@frontegg/react";
import { useDispatch, useSelector } from "react-redux";
import AppLoader from "components/AppLoader/Index";
import { loginRequestFrontegg } from "./Redux/Actions/LoginActions";
import { setFronteggAccessToken } from "./authSession";

const PUBLIC_PATHS = [
  "/invite",
  "/sessionOut",
  "/invalidURL",
  "/resetPassword",
  "/admin/email/register",
  "/errorCodeOpen",
  "/payment/verify/link",
  "/payment/process",
  "/payment/failure",
  "/tsc/payment",
  "/tpo/payments",
  "/makePayment",
  "/paymentDetails",
];

const isPublicPath = (pathname) => (
  PUBLIC_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))
);

function AuthBootstrap({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const loginWithRedirect = useLoginWithRedirect();
  const dispatch = useDispatch();

  const loggedIn = useSelector((state) => state.app.loggedIn);
  const appToken = useSelector((state) => state.app.token);
  const authError = useSelector((state) => state.app.authError);
  const accessToken = user && user.accessToken;
  localStorage.setItem("fronteggAccessToken", `Bearer ${accessToken}`);
  const loginTriggeredRef = React.useRef(false);
  const syncingTokenRef = React.useRef(null);

  const pathname = window.location.pathname;
  const isInvite = pathname === "/invite";
  const isOAuthCallback = pathname === "/oauth/callback";
  const publicPath = isPublicPath(pathname);

  React.useEffect(() => {
    setFronteggAccessToken(accessToken);
  }, [accessToken]);

  React.useEffect(() => {
    if (isInvite || publicPath) return;
    if (isLoading || isOAuthCallback) return;
    if (isAuthenticated) return;
    if (loginTriggeredRef.current) return;

    loginTriggeredRef.current = true;
    loginWithRedirect();
  }, [
    isAuthenticated,
    isInvite,
    isLoading,
    isOAuthCallback,
    loginWithRedirect,
    publicPath,
  ]);

  React.useEffect(() => {
    if (isInvite || publicPath) return;
    if (isLoading || !isAuthenticated || !accessToken) return;
    if (loggedIn && appToken) {
      return;
    }
    if (syncingTokenRef.current === accessToken) return;

    syncingTokenRef.current = accessToken;

    dispatch(loginRequestFrontegg(accessToken))
      .then(() => {
        // backend sync success
      })
      .catch(() => {
        // backend sync failed
      })
      .finally(() => {
        if (syncingTokenRef.current === accessToken) {
          syncingTokenRef.current = null;
        }
      });
  }, [
    appToken,
    dispatch,
    isAuthenticated,
    isInvite,
    isLoading,
    loggedIn,
    publicPath,
    accessToken,
  ]);

  if (isInvite || publicPath) {
    return children;
  }

  if (authError && !loggedIn) {
    return children;
  }

  if (isLoading || isOAuthCallback) {
    return <AppLoader loader />;
  }

  if (!isAuthenticated) {
    return <AppLoader loader />;
  }

  if (!loggedIn || !appToken) {
    return <AppLoader loader />;
  }

  return children;
}

export default AuthBootstrap;
